/* ================================================================
 * Tour stop · MatchState vs MatchSnapshot, branded IDs, Readonly<>
 * ----------------------------------------------------------------
 * See docs/code-roadmap.md §3.3 and §5.6 for the surrounding
 * narrative.
 *
 * Two TypeScript ideas land in this one file, and both are central
 * to the codebase. Read them carefully before changing any state
 * shape.
 *
 * 1) Branded ID types (see also packages/shared/src/ids.ts).
 *    `PlayerId` is defined as `string & { __brand: "PlayerId" }`.
 *    At runtime it's a plain string. At compile time it's a
 *    DIFFERENT TYPE from a bare string — you can't accidentally pass
 *    a SeatId where a PlayerId is expected, even though both are
 *    strings. The only way to construct one is via the `playerId(s)`
 *    helper, which is just `s as PlayerId` under the hood. This is
 *    the closest TypeScript gets to nominal types (C# enums, Rust
 *    newtypes). It costs zero runtime overhead.
 *
 * 2) MatchState (mutable) vs MatchSnapshot (Readonly).
 *    `MatchState` is the simulation's authoritative live state.
 *    `tickMatch` mutates it in place; that's deliberate, performance-
 *    motivated, and allowed by the AGENTS.md "purity relative to
 *    arguments" rule.
 *
 *    `MatchSnapshot` is what `takeSnapshot(state)` returns: a deep
 *    `structuredClone` of (most of) MatchState, wrapped in
 *    `Readonly<>` at every level. The renderer receives a
 *    MatchSnapshot. At compile time, TypeScript refuses any code
 *    that tries to mutate it. At runtime, mutation would only affect
 *    the renderer's own copy anyway, because of the deep clone.
 *
 *    Notice what MatchSnapshot OMITS compared to MatchState: `rng`.
 *    The renderer has no business knowing the PRNG state. `control`
 *    is included — the local renderer needs to know which player is
 *    human-controlled to highlight them and steer the camera. A
 *    networked model in Phase E+ will project `control` per-seat so a
 *    remote client only sees its own control assignments, but that's
 *    the wire format's problem, not this type's.
 *
 * Discriminated unions also appear here: `PlayerAction` is a union
 * of literal strings, narrowed by the compiler when you switch on it.
 * See packages/shared/src/events.ts (tour stop #7) for the full
 * pattern applied to MatchEvent.
 *
 * If you add a gameplay-affecting field, you must (a) add it to
 * MatchState, (b) decide whether the snapshot should include it,
 * and (c) mix it into the per-tick hash in core/SimTick.ts. AGENTS.md
 * spells this out as a hard rule.
 * ================================================================ */

import type { PlayerId, TeamId } from "@arcade-hockey/shared";
import type { MatchEvent } from "@arcade-hockey/shared";
import type { RngState } from "../math/Random.js";
import type { Vec2 } from "../math/Vec2.js";
import type { FaceoffSpot } from "./faceoff-spots.js";
import type { SkaterRole } from "./roles.js";

export type MatchClockPhase =
  | "Faceoff"
  | "Play"
  | "Stoppage"
  | "GoalCelebration"
  | "PeriodBreak"
  | "Overtime"
  | "Shootout"
  | "GameOver";

// Phase G slice 6: per-team tactical scheme. Drives how non-engaged bots
// position themselves on top of their (role × situation) anchor. New
// tactics are added by defining their modifier inside
// packages/bots/src/positioning/home-position.ts; the data itself stays
// on TeamControlState and is hashed so changes are deterministic and
// replay-safe.
export type TacticId = "balanced" | "trap" | "aggressive";

export const TACTIC_IDS: ReadonlyArray<TacticId> = ["balanced", "trap", "aggressive"];

export type MatchClock = {
  periodIndex: 0 | 1 | 2;
  remainingTicks: number;
  phase: MatchClockPhase;
  /**
   * Slice 8 — the next "live play" phase to enter when the current
   * Faceoff phase resolves. `"Play"` is the regulation default;
   * `"Overtime"` is set by `enterOvertime` so the post-faceoff Play
   * branch starts the 60-second OT clock instead of a regulation
   * period clock. Undefined → defaults to "Play".
   */
  nextPlayPhase?: "Play" | "Overtime";
};

export type ScoreState = {
  home: number;
  away: number;
};

export type RinkConsts = {
  length: number;
  width: number;
  cornerRadius: number;
  goalMouthHalfWidth: number;
  goalDepth: number;
};

export type PuckMode = "Loose" | "Controlled" | "Pass" | "Shot";

export type PuckState = {
  pos: Vec2;
  vel: Vec2;
  mode: PuckMode;
  ownerId: PlayerId | null;
  lastTouchedBy: PlayerId | null;
  noRecaptureUntilTick: number;
  /**
   * Faceoff centre-lockout — after a faceoff pop, both teams' centres
   * are "tied up" in the draw briefly and cannot claim the puck. This
   * forces the puck to actually reach the targeted teammate (winger
   * or defenseman) rather than be snagged by whichever centre is
   * closest to the dot. Cleared (set to 0) once the lockout expires.
   * See `rules/faceoff.ts:resolveFaceoffPop`.
   */
  faceoffCenterLockoutUntilTick: number;
  /**
   * Per-opponent recapture lockout (Slice 4 anti-flicker).
   *
   * When player A strips / fumbles / picks-up the puck from player B,
   * B is locked from claiming the puck for
   * `tuning.puck.sameOpponentRecaptureTicks` ticks. Other players are
   * unaffected. Prevents the A→B→A→B ping-pong cycle that haunted
   * CPU-vs-CPU centre-ice duels in earlier slices.
   *
   * Cleared (set to null / 0) when the lockout expires or play stops.
   */
  opponentLockoutPlayerId: PlayerId | null;
  opponentLockoutUntilTick: number;
};

export type PlayerAction =
  | "Idle"
  | "WindupPass"
  | "WindupShoot"
  | "WindupCheck"
  | "RecoverCheck"
  | "Stunned";

export type PlayerState = {
  id: PlayerId;
  teamId: TeamId;
  // Static positional role (LW/C/RW/LD/RD). Set at match creation; immutable
  // for the duration of the match. Drives positional anchoring in the bot
  // layer; later Phase G slices add situation + job layered on top.
  position: SkaterRole;
  pos: Vec2;
  vel: Vec2;
  moveDir: Vec2;
  faceDir: Vec2;
  stickDir: Vec2;
  action: PlayerAction;
  actionTimer: number;
  stunTicks: number;
  recoveryTicks: number;
  stamina: number;
  hasPuck: boolean;
  possessionQuality: number;
  shootHoldTicks: number;
  passHoldTicks: number;
  receivedPassFromTick: number;
  isProtecting: boolean;
  /**
   * Slice 8 — Overtime / Shootout off-ice marker. Wings (LW/RW) sit in
   * the bench during 3-on-3 OT and shootout rounds; benched players
   * skip skating, possession, body checks, board collisions, and bot
   * inputs. Their `pos` is parked just past the board edge so the
   * renderer can show them on the bench (or not at all). False during
   * regulation play.
   */
  benched: boolean;
};

export type GoalieMode = "Ready" | "Tracking" | "Saving" | "Recovering";

export type GoalieState = {
  id: PlayerId;
  teamId: TeamId;
  pos: Vec2;
  vel: Vec2;
  facing: Vec2;
  mode: GoalieMode;
  modeTimer: number;
  pulled: boolean;
};

/**
 * Per-seat human-controlled player. Fixed-shape (two seats today) rather than
 * a Map so the hot path can index P1/P2 directly and structuredClone'd
 * snapshots skip the per-tick Map clone. `null` means the seat is unassigned.
 */
export type SeatControlMap = {
  P1: PlayerId | null;
  P2: PlayerId | null;
};

export const SEAT_IDS = ["P1", "P2"] as const;
export type SeatIdKey = (typeof SEAT_IDS)[number];

export type TeamControlState = {
  teamId: TeamId;
  controlledByHuman: SeatControlMap;
  lastManualSwitchTick: number;
  manualGoalieSeat: string | null;
  goaliePullToggleHeldLastTick: boolean;
  // Phase G slice 6: per-team tactical scheme. Read by the bot anchor
  // path and used to bias positions on top of (role × situation) cells.
  // Hidden hotkeys in the client cycle this for CPU-vs-CPU iteration.
  tactic: TacticId;
};

/**
 * Per-rule scratch state. Every rule under `packages/sim/src/rules/` that
 * needs to remember something across ticks lands its fields here, keyed by
 * the rule name. Keeps `MatchState` from sprouting flat fields every time a
 * new rule lands.
 *
 * Conventions:
 *  - Each rule owns one optional sub-field (`offside?`, `icing?`,
 *    `nextFaceoffSpot?`, `shootout?`, …).
 *  - Sub-fields are optional so a fresh match without that rule active
 *    doesn't pay any size/storage cost. Rules that need always-present
 *    state can declare it required.
 *  - The whole `rules` object is mixed into the per-tick hash wholesale
 *    (see `core/SimTick.ts:computeHash`), so determinism is preserved
 *    without per-field plumbing.
 *
 * Initially empty (`{}`). Slice 0 adds `nextFaceoffSpot`; subsequent
 * slices fill in the rest.
 */
export type RulesState = {
  /**
   * Faceoff spot for the upcoming Faceoff phase. Set by `enterStoppage`
   * (see `rules/stoppage.ts`) when a rule triggers a stoppage. Read by
   * the Stoppage → Faceoff transition in `core/SimTick.ts` Step 18 to
   * pick which per-spot player layout to apply.
   *
   * Optional because the existing post-goal flow doesn't go through
   * `enterStoppage` — it transitions GoalCelebration → Faceoff directly
   * with the implicit Centre dot. When undefined, the Faceoff layout
   * defaults to Centre, preserving the pre-rules-pass behaviour.
   */
  nextFaceoffSpot?: FaceoffSpot;
  /**
   * Per-team faceoff intent tracker, populated during the Faceoff
   * phase. Each centre selects a target teammate via stick direction
   * (forward+left → LW, forward+right → RW, back+left → LD, back+right
   * → RD) and locks in the commit by pressing Pass. Stability builds
   * up while the same target is held; resets on target change.
   *
   * At the Faceoff → Play transition, `rules/faceoff.ts:resolveFaceoffPop`
   * scores each team's commit (timing + stability) and pops the puck
   * toward the winner's target. Cleared after resolution.
   */
  faceoffIntents?: {
    home?: FaceoffIntentTracker;
    away?: FaceoffIntentTracker;
  };
  /**
   * Pending offside state — set when the puck crosses a blue line
   * while an attacking teammate (other than the carrier) is already in
   * the OZ. Cleared when the puck leaves the OZ, all potential players
   * tag up, or the offside whistle fires. See `rules/offside.ts` for
   * the per-tick resolution.
   */
  offside?: PendingOffside;
  /**
   * Armed icing state — set when a player shoots the puck from their
   * own half of centre ice. Cleared when (a) any other player touches
   * the puck (including a teammate), (b) the puck crosses the opposing
   * goal line untouched → ICING whistle, (c) play stops for other
   * reasons. See `rules/icing.ts`.
   */
  icing?: IcingArm;
  /**
   * Cross-tick stall counter for `rules/puck-out-of-play.ts`. Lazy-
   * initialised on first qualifying tick; cleared the moment the puck
   * starts moving / leaves the corner / a skater enters the radius.
   * Reaches the threshold → PuckOutOfPlay whistle.
   */
  puckOutOfPlay?: { ticks: number };
  /**
   * Shootout state — set by `enterShootout` when overtime expires
   * tied. Drives the round / attempt machinery in `rules/shootout.ts`.
   * Cleared at `GameOver`.
   */
  shootout?: ShootoutState;
};

/**
 * Shootout state — see `rules/shootout.ts`. Each shootout is a sequence
 * of alternating attempts (home, away, home, away, ...). The first 3
 * rounds (6 attempts) are scheduled in advance; if still tied, sudden-
 * death rounds continue one attempt each until the score differs.
 *
 * Sub-phase machine inside an attempt:
 *   "Setup"  — reset positions, brief pause (~30 ticks)
 *   "Shoot"  — shooter has the rest of the attempt budget to score
 *   "Result" — show goal / miss for a beat before the next attempt
 *
 * One PlayerId-typed `shooterId` is denormalised from the team + index
 * for fast access.
 */
export type ShootoutSubPhase = "Setup" | "Shoot" | "Result";

export type ShootoutState = {
  /** 1-indexed round number; rounds 1..3 are the formal three; 4+ are sudden death. */
  round: number;
  /** Which team is shooting the current attempt. */
  shooterTeam: TeamId;
  /** PlayerId of the current shooter. */
  shooterId: PlayerId;
  /** Sub-phase machine. */
  subPhase: ShootoutSubPhase;
  /** Tick when the current sub-phase ends and we transition. */
  subPhaseEndTick: number;
  /** Outcome of the most recently completed attempt — drives the Result sub-phase. */
  lastResult: "Goal" | "Miss" | "None";
  /** Cumulative shootout score. */
  score: { home: number; away: number };
  /** Counts of attempts taken per team. */
  attempts: { home: number; away: number };
  /**
   * True once a `ShotReleased` event from the current shooter has
   * been observed in this attempt. The shooter gets one shot only —
   * after this fires, they can't reclaim the puck for a second
   * attempt, and the attempt resolves to Goal or Miss within
   * `tuning.clock.shootoutPostShotTicks`.
   */
  shotTaken: boolean;
  /** Tick when `shotTaken` first flipped to true; -1 if not yet shot. */
  shotTakenAtTick: number;
  /**
   * Furthest-forward puck-x reached this attempt (signed by team).
   * For HOME (attacks +x) this stores `puck.pos.x`; for AWAY it
   * stores `-puck.pos.x`. The attempt ends as Miss if the puck
   * regresses by more than `tuning.clock.shootoutMaxRegression` from
   * this peak — the puck-must-move-forward rule.
   */
  maxPuckForward: number;
};

/**
 * Faceoff target — one of the four non-centre roles the centre can
 * direct the puck toward. The stick quadrant maps:
 *   forward + left  → LW       forward + right → RW
 *   back    + left  → LD       back    + right → RD
 *
 * "Forward" means the centre's attacking direction (+x for HOME, -x for
 * AWAY). "Left" means +y in world coords (matches the spawn convention
 * for LW / LD on both teams).
 */
export type FaceoffTarget = "LW" | "RW" | "LD" | "RD";

/**
 * Per-team scratch state during the Faceoff phase.
 *
 *   `currentTarget` — the stick-direction pick this tick. Null when
 *     the stick is too close to neutral to pick a quadrant.
 *   `targetSetAtTick` — tick when `currentTarget` was most recently
 *     set / changed. Used to derive stability at commit time.
 *   `passHeldLastTick` — edge-detection for Pass: a rising edge
 *     (`false → true`) latches the commit.
 *   `commit` — set once the player has hit Pass; immutable afterwards
 *     for the rest of the faceoff window.
 */
export type FaceoffIntentTracker = {
  currentTarget: FaceoffTarget | null;
  targetSetAtTick: number;
  passHeldLastTick: boolean;
  commit: FaceoffCommit | null;
};

export type FaceoffCommit = {
  target: FaceoffTarget;
  /** Tick when Pass was pressed (the commit tick). */
  commitTick: number;
  /** How many consecutive ticks the target was held before commit. */
  stabilityTicks: number;
};

/**
 * Pending offside — see `rules/offside.ts`. Defined here (rather than
 * in the rules module) so `RulesState` doesn't import from `rules/`,
 * which would create a circular type reference back to `MatchState`.
 */
export type PendingOffside = {
  /** The attacking team in violation. */
  team: TeamId;
  /** Skaters who were in the OZ at the moment the puck crossed. */
  potentialIds: PlayerId[];
  /** Where the puck was at the blue-line crossing — used to pick the NZ dot. */
  puckEnterX: number;
  puckEnterY: number;
};

/**
 * Armed icing — see `rules/icing.ts`. The shooter fired the puck from
 * their own half of centre ice; if it crosses the opposing goal line
 * untouched, an icing whistle blows.
 */
export type IcingArm = {
  /** Team that armed the icing (the shooter's team). */
  team: TeamId;
  /** Shooter's id — used to detect a "touched-by-someone-else" cancel. */
  shooterId: PlayerId;
  /** y-coord of the shot's origin — picks the side of the DZ dot. */
  shotFromY: number;
};

export type MatchState = {
  tick: number;
  clock: MatchClock;
  rng: RngState;
  score: ScoreState;
  rink: RinkConsts;
  puck: PuckState;
  players: PlayerState[];
  goalies: GoalieState[];
  control: TeamControlState[];
  rules: RulesState;
  events: MatchEvent[];
  hash: number;
  /**
   * Derived lookup cache: PlayerId → PlayerState. Built once at createMatch
   * after `players` is canonically sorted; consult via getPlayerById. Not
   * mixed into `hash`, not included in `MatchSnapshot`. If roster changes at
   * runtime (Phase F), rebuild via rebuildPlayersById.
   */
  playersById: Map<PlayerId, PlayerState>;
};

// ----------------------------------------------------------------
// Contrived starting-state overrides — see applySetup in core/SimTick.ts
// and the `setup` field on Replay. Demo/test authors hand-place the puck
// and players before tick 0; every field is optional, only those present
// override the post-createMatch defaults.
// ----------------------------------------------------------------

export type SetupPuck = Partial<PuckState>;

// `id` selects which player to override; other fields are partial overrides.
// `teamId` is intentionally not overridable.
export type SetupPlayer = { id: PlayerId } & Partial<Omit<PlayerState, "id" | "teamId">>;

export type SetupGoalie = { id: PlayerId } & Partial<Omit<GoalieState, "id" | "teamId">>;

export type SetupControl = {
  teamId: TeamId;
  controlledByHuman?: Partial<SeatControlMap>;
};

export type ReplaySetup = {
  clock?: Partial<MatchClock>;
  /**
   * Scoreboard override — seed the match score before tick 0. Used
   * by scenarios that exercise score-dependent rules (auto-pull,
   * regulation-winner) without having to fabricate a score sequence.
   */
  score?: Partial<ScoreState>;
  puck?: SetupPuck;
  players?: ReadonlyArray<SetupPlayer>;
  goalies?: ReadonlyArray<SetupGoalie>;
  control?: ReadonlyArray<SetupControl>;
  /**
   * Per-rule scratch state overrides. Each rule slice that adds a sub-field
   * to `RulesState` should also accept it here so scenario replays can seed
   * the rule's state without having to fabricate the position chain that
   * would otherwise produce it.
   */
  rules?: Partial<RulesState>;
};

// A read-only view of MatchState for the renderer. In FVV this is just
// structuredClone(state) at snapshot time; later it becomes the wire shape.
export type MatchSnapshot = Readonly<{
  tick: number;
  clock: Readonly<MatchClock>;
  score: Readonly<ScoreState>;
  puck: Readonly<PuckState>;
  players: ReadonlyArray<Readonly<PlayerState>>;
  goalies: ReadonlyArray<Readonly<GoalieState>>;
  control: ReadonlyArray<Readonly<TeamControlState>>;
  rules: Readonly<RulesState>;
  events: ReadonlyArray<MatchEvent>;
  hash: number;
}>;
